<template>
  <div :class="$style.dropDownListContainer">
    <div
      v-show="!loading && !errorText"
      :class="$style.dropDownListContent"
    >
      <div
        v-if="includeSearch"
        :class="$style.searchContainer"
      >
        <input
          v-model="searchText"
          :class="[$style.searchInput, $style.input]"
          data-test-id="MultiselectMenu__searchInput"
          type="text"
          @input="handleSearch"
        >
        <SearchIcon
          :class="$style.searchIcon"
        />
        <CloseIcon
          :class="$style.clearSearchIcon"
          @click="handleSearchClear"
        />
      </div>
      <div :class="$style.resultsContainer">
        <div
          v-if="multiselect"
          v-show="!searchText && selectedOptions.length"
          :class="{
            [$style.selectedOptionsListWithAvailableShowing]: availableOptions.length,
          }"
        >
          <label :class="$style.listLabel">
            Selected ({{ selectedOptions.length }})
            <span
              v-if="maxOptionsSelectable"
              :class="$style.maxOptionsSelectableText"
            >
              {{ maxOptionsSelectable }} max
            </span>
          </label>
          <ul :class="$style.dropDownList">
            <li
              v-for="option in selectedOptions"
              :key="option.value"
              data-test-id="MultiselectMenu__selectedOption"
            >
              <label :class="$style.dropDownRow">
                <input
                  :checked="true"
                  :disabled="option.required"
                  type="checkbox"
                  @change="handleChange(option)"
                >
                <span>{{ option.name }}</span>
              </label>
            </li>
          </ul>
        </div>
        <div>
          <label
            v-if="multiselect"
            v-show="!searchText && availableOptions.length"
            :class="$style.listLabel"
          >
            Available ({{ availableOptions.length }})
          </label>
          <ul
            :class="[$style.dropDownList, {
              [$style.dropDownListDisabled]: selectingDisabled,
            }]"
          >
            <li
              v-for="option in availableOptions.slice(0, maxResultsToShow)"
              :key="option.value"
              data-test-id="MultiselectMenu__option"
            >
              <label
                :class="$style.dropDownRow"
              >
                <input
                  :checked="valuesSet.has(option.value)"
                  :disabled="selectingDisabled"
                  :type="multiselect ? 'checkbox' : 'radio'"
                  @change="handleChange(option, !valuesSet.has(option.value))"
                >
                <span :title="option.name">{{ option.name }}</span>
              </label>
            </li>
          </ul>
        </div>
        <div
          v-if="!options.length"
          :class="$style.errorText"
        >
          <slot name="noOptionsAvailableMessage">
            No options available
          </slot>
        </div>
        <div
          v-show="noResultsFound"
          :class="$style.errorText"
        >
          No results found
        </div>
      </div>
      <div
        v-if="includeSearch"
        v-show="!noResultsFound"
        :class="$style.numResultsText"
      >
        {{ numResultsText }}
        Search to narrow results.
        <slot name="footer" />
      </div>
    </div>
    <div
      v-show="loading"
      :class="$style.loadingContainer"
    >
      <LoadingIcon
        :class="$style.loadingIcon"
      />
    </div>
    <div
      v-show="!loading && errorText"
      :class="$style.errorText"
    >
      {{ errorText }}
    </div>
  </div>
</template>

<script>
import CloseIcon from 'public/img/icons/close.svg?inline';
import Fuse from 'fuse.js';
import LoadingIcon from 'public/img/icons/loading.svg?inline';
import SearchIcon from 'public/img/icons/icon-search.svg?inline';
import pluralize from 'pluralize';

export default {
  name: 'MultiSelectMenu',
  components: {
    CloseIcon,
    LoadingIcon,
    SearchIcon,
  },
  props: {
    alwaysShowSearch: {
      type: Boolean,
      default: false,
    },
    errorText: {
      type: String,
      default: '',
    },
    loading: {
      type: Boolean,
      default: false,
    },
    maxOptionsSelectable: {
      type: Number,
      default: 0,
    },
    multiselect: {
      type: Boolean,
      default: true,
    },
    numResultsText: {
      type: String,
      default: '',
    },
    /* `options` is Array<{ name: string, value: string }> of all available menu items */
    options: {
      type: Array,
      default: () => [],
    },
    sortOptions: {
      type: Boolean,
      default: true,
    },
    /* `value` is Array<string> of each selected menu item */
    value: {
      type: Array,
      default: () => [],
    },
  },

  data: () => ({
    maxResultsToShow: 500,
    searchText: '',
  }),

  computed: {
    availableOptions() {
      const options = (() => {
        if (!this.searchText && !this.multiselect) return this.options;
        if (!this.searchText && this.multiselect) {
          // Only show options that have not been selected
          return this.options.filter(d => !this.valuesSet.has(d.value));
        }
        return this.matchingOptions;
      })();
      if (this.sortOptions) this.sortOptionsByName(options);
      return options;
    },

    includeSearch() { return this.options.length > 10 || this.alwaysShowSearch; },

    noResultsFound() { return Boolean(this.searchText && !this.availableOptions.length); },

    pluralizedResultText() {
      return pluralize('result', this.options.length);
    },

    selectedOptions() {
      const selectedOptions = this.options.reduce((acc, option) => {
        if (this.valuesSet.has(option.value)) acc.push(option);

        return acc;
      }, []);

      return this.sortOptions
        ? this.sortOptionsByName(selectedOptions)
        : selectedOptions;
    },

    selectingDisabled() {
      return (
        !!this.maxOptionsSelectable
        && (this.value.length >= this.maxOptionsSelectable)
      );
    },

    valuesSet() { return new Set(this.value); },
  },

  methods: {
    handleChange(option, checked = false) {
      // disallow if selecting disabled and new option is selected
      if (this.selectingDisabled && checked) return;

      let selectedOptions = [...this.value];

      const index = this.value.indexOf(option.value);
      if (index === -1 && checked) {
        // Selecting an option
        if (this.multiselect) {
          selectedOptions.push(option.value);
        } else {
          selectedOptions = [option.value];
        }
      } else if (index > -1 && !checked && this.multiselect && !option.required) {
        // Un-selecting an option
        selectedOptions.splice(index, 1);
      }

      this.$emit('input', selectedOptions);
    },

    handleSearch(event) {
      const fuse = new Fuse(this.options, {
        includeScore: true, // Score of 0 is a perfect match. Score of 1 means no match.
        keys: ['name', 'value'],
        location: 1000, // The search text can be up to 1000 characters away from the beginning char
        threshold: 0.4,
      });
      const results = fuse.search(event.target.value);
      this.matchingOptions = results.map(d => d.item);
    },

    handleSearchClear() {
      this.searchText = '';
    },

    sortOptionsByName(options) {
      return [...options].sort((a, b) => {
        if (a.name < b.name) return -1;
        if (a.name > b.name) return 1;
        return 0;
      });
    },
  },
};
</script>

<style module>
@import '../../FormValidation/css/FormValidationFields.css';

@value (gradientDark4, gradientLight3, gradientLight4) from '~css/colors.css';
@value (cardShadow1) from '~css/shadows.css';

.dropDownListContainer {
  background-color: white;
  border: 1px solid gradientLight4;
  border-radius: 4px;
  box-shadow: cardShadow1;
  display: flex;
  flex-direction: column;
  min-width: 250px;
}

.dropDownListContent {
  display: flex;
  flex: 1;
  flex-direction: column;
  min-height: 0;
}

.clearSearchIcon {
  cursor: pointer;
  opacity: 0.7;
  position: absolute;
  right: 18px;
  top: 18px;
  width: 14px;
}

.clearSearchIcon:focus { outline: none; }

.dropDownList {
  margin: 0 !important;
  padding: 0;
}

.dropDownList li {
  list-style-type: none;
  padding-left: 10px;
}

.dropDownList li:hover { background-color: gradientLight3; }
.dropDownListDisabled { color: gradientDark4; }
.dropDownListDisabled li:hover { background-color: inherit; }

.searchContainer input[type='text'] {
  background-color: white;
  font-size: 13px;
  padding: 4px 9px 3px 29px;
  width: 100%;
}

.dropDownRow {
  display: block;
}

.dropDownRow > *:not(span) { vertical-align: middle; }
.dropDownRow input:focus { outline: none; }

.dropDownRow input[type="checkbox"],
.dropDownRow input[type="radio"] {
  margin: 0 4px 0 0;
}

.errorText {
  font-size: 13px;
  font-style: italic;
  padding: 2em 1em;
  text-align: center;
}

label.dropDownRow {
  font-size: 13px;
  font-weight: 400;
  margin: 2px 0 3px;
  overflow-x: hidden;
  text-overflow: ellipsis;
  vertical-align: top;
  white-space: nowrap;
}

.listLabel {
  font-size: 13px;
  margin-bottom: 2px !important;
}

.loadingContainer {
  padding: 40px;
  text-align: center;
}

.loadingIcon {
  animation: spin 2s infinite linear;
  fill: var(--jcPrimary);
  width: 22px;
}

.maxOptionsSelectableText {
  font-size: 11px;
  font-weight: 400;
  margin-left: 2px;
}

.numResultsText {
  border-top: 1px solid gradientLight4;
  box-shadow: 0 -1px 3px rgba(0, 0, 0, 0.05);
  font-size: 13px;
  font-style: italic;
  font-weight: 600;
  padding: 8px 16px;
  text-align: center;
}

.resultsContainer {
  flex: 1;
  min-height: 0;
  overflow: auto;
  padding: 10px;
}

.selectedOptionsListWithAvailableShowing { margin-bottom: 10px; }

.searchContainer {
  padding: 10px 10px 0;
  position: relative;
}

.searchIcon {
  height: 14px;
  left: 19px;
  position: absolute;
  top: 18px;
  width: 14px;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}
</style>
